import { ApplicationGoogleDoc } from "@prisma/client";

import { AI_REF_LETTER_DOC_TYPE } from "@/lib/google-doc";
import { prisma } from "@/lib/prisma";

import { normalizeAiRefLetterWebhookBody } from "./normalizeAiRefLetterWebhookBody";

export type AiRefLetterWebhookPayload = {
  googleDocUrl: string;
  googleDriveId: string;
  applicationId: string;
  refereeId: string;
  googleDocVersion?: string;
  clientName?: string;
  refereeFullName?: string;
  fileName?: string;
  googleDriveFolderUrl?: string;
};

export type SaveAiRefLetterResult = {
  aiDocument: ApplicationGoogleDoc;
  refereeName: string;
  userId: string;
  mondayClientItemId: string | null;
};

export class AiRefLetterWebhookValidationError extends Error {
  missingFields: string[];

  constructor(missingFields: string[]) {
    super(`Missing required fields: ${missingFields.join(", ")}`);
    this.name = "AiRefLetterWebhookValidationError";
    this.missingFields = missingFields;
  }
}

export class AiRefLetterWebhookNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AiRefLetterWebhookNotFoundError";
  }
}

function extractGoogleDriveIdFromUrl(url: string): string | null {
  const match = url.match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
  return match?.[1] ?? null;
}

function validatePayload(
  body: Record<string, any> | null | undefined
): AiRefLetterWebhookPayload {
  const applicationId = body?.applicationId?.toString().trim() ?? "";
  const refereeId = body?.refereeId?.toString().trim() ?? "";
  const googleDocUrl = body?.googleDocUrl?.toString().trim() ?? "";

  const missingFields: string[] = [];
  if (!applicationId) missingFields.push("applicationId");
  if (!refereeId) missingFields.push("refereeId");
  if (!googleDocUrl) missingFields.push("googleDocUrl");

  if (missingFields.length > 0) {
    throw new AiRefLetterWebhookValidationError(missingFields);
  }

  const googleDriveId =
    body?.googleDriveId?.toString().trim() ||
    body?.googleDocId?.toString().trim() ||
    extractGoogleDriveIdFromUrl(googleDocUrl) ||
    "";

  if (!googleDriveId) {
    throw new AiRefLetterWebhookValidationError(["googleDriveId"]);
  }

  return {
    googleDocUrl,
    googleDriveId,
    applicationId,
    refereeId,
    googleDocVersion: body?.googleDocVersion?.toString().trim() || undefined,
    clientName: body?.clientName?.toString().trim() || undefined,
    refereeFullName: body?.refereeFullName?.toString().trim() || undefined,
    fileName: body?.fileName?.toString().trim() || undefined,
    googleDriveFolderUrl:
      body?.googleDriveFolderUrl?.toString().trim() || undefined,
  };
}

export async function saveAiRefLetterFromWebhook(
  rawBody: Record<string, any> | null | undefined
): Promise<SaveAiRefLetterResult> {
  const normalizedBody = normalizeAiRefLetterWebhookBody(rawBody);
  const payload = validatePayload(normalizedBody);

  const application = await prisma.application.findUnique({
    where: { id: payload.applicationId },
    include: {
      applicant: {
        include: {
          user: true,
          applicantReferee: {
            where: {
              id: payload.refereeId,
              deleted: null,
            },
            take: 1,
          },
        },
      },
    },
  });

  if (!application) {
    throw new AiRefLetterWebhookNotFoundError("Application not found");
  }

  if (!application.applicant.applicantReferee?.length) {
    throw new AiRefLetterWebhookNotFoundError("Referee not found");
  }

  const referee = application.applicant.applicantReferee[0];
  const createdByUserId = application.applicant.user.id;
  const refereeName =
    payload.refereeFullName ||
    `${referee.firstName ?? ""} ${referee.lastName ?? ""}`.trim() ||
    "Referee";
  const fileName =
    payload.fileName || `${refereeName}_ai-ref-letter`.replace(/\s+/g, "_");

  const meta = {
    refereeId: payload.refereeId,
    refereeName,
    source: "n8n",
    ...(payload.googleDocVersion && {
      googleDocVersion: payload.googleDocVersion,
    }),
    ...(payload.clientName && { clientName: payload.clientName }),
    ...(payload.googleDriveFolderUrl && {
      googleDriveFolderUrl: payload.googleDriveFolderUrl,
    }),
  };

  // Always create a new row — regenerations keep previous AI letter links
  const aiDocument = await prisma.applicationGoogleDoc.create({
    data: {
      applicationId: payload.applicationId,
      type: AI_REF_LETTER_DOC_TYPE,
      googleDriveId: payload.googleDriveId,
      url: payload.googleDocUrl,
      fileName,
      meta,
      userId: createdByUserId,
      completionStatus: "in_progress",
    },
  });
  console.log(
    `✅ Created AI ref letter doc: id=${aiDocument.id}, refereeId=${payload.refereeId}`
  );

  return {
    aiDocument,
    refereeName,
    userId: createdByUserId,
    mondayClientItemId: application.applicant.user.mondayClientItemId ?? null,
  };
}
